// src/pages/Home.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs,
} from "firebase/firestore";
import HeroSection from "../components/HeroSection";

/* ---------- Helpers contact ---------- */
const WAPP_NUMBER = "22800000000";
const SALES_EMAIL = "contact@mameche.tg";
const wapp = (txt) => `https://wa.me/${WAPP_NUMBER}?text=${encodeURIComponent(txt)}`;
const mailto = (subject, body) =>
  `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

/* ---------- UI ---------- */
const PAGE_SIZE = 60;
// quotas d’affichage
const MED_LIMIT = 9;
const IND_LIMIT = 6;

const BRANDS = [
  { name: "Mindray", src: "https://via.placeholder.com/140x60?text=Mindray" },
  { name: "Camry", src: "https://via.placeholder.com/140x60?text=Camry" },
  { name: "3M", src: "https://via.placeholder.com/140x60?text=3M" },
  { name: "Omron", src: "https://via.placeholder.com/140x60?text=Omron" },
  { name: "Roche", src: "https://via.placeholder.com/140x60?text=Roche" },
  { name: "Siemens", src: "https://via.placeholder.com/140x60?text=Siemens" },
];

/* ---------- micro hooks ---------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}
function useCountUp(target = 0, inView = false, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);
  return val;
}

/* ---------- petits composants ---------- */
const SectionTitle = ({ title, subtitle, center }) => (
  <div className={center ? "text-center mb-4" : "mb-3"}>
    <h2 className="fw-bold mb-1">{title}</h2>
    {subtitle && <small className="text-muted">{subtitle}</small>}
  </div>
);
const ServiceCard = ({ icon, title, text }) => {
  const [ref, seen] = useInView();
  return (
    <div
      ref={ref}
      className="card h-100 shadow-sm border-0 cta-lift"
      style={{
        opacity: seen ? 1 : 0,
        transform: seen ? "none" : "translateY(8px)",
        transition: "opacity .45s ease, transform .45s ease",
      }}
    >
      <div className="card-body text-center">
        <div className="display-6" aria-hidden="true">{icon}</div>
        <h6 className="fw-bold mt-2 mb-1">{title}</h6>
        <p className="small text-muted mb-2">{text}</p>
        <Link className="btn btn-outline-primary btn-sm" to="/contact" onClick={() => window.scrollTo(0, 0)}>
          Obtenir un devis
        </Link>
      </div>
    </div>
  );
};

export default function Home() {
  /* -------- Slides -------- */
  const [slides, setSlides] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(true);

  /* -------- Produits -------- */
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  /* -------- Projets & avis -------- */
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  /* -------- SEO -------- */
  const SITE_URL =
    typeof window !== "undefined" ? window.location.origin : "https://mameche.tg";

  /* -------- Firestore: Slides -------- */
  useEffect(() => {
    const col = collection(db, "homeSlides");
    const qy = query(col, orderBy("createdAt", "desc"), limit(6));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setSlides(arr);
        setLoadingSlides(false);
      },
      () => setLoadingSlides(false)
    );
    return () => unsub();
  }, []);

  /* -------- Firestore: Produits -------- */
  useEffect(() => {
    const col = collection(db, "products");
    const qy = query(col, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const docs = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            dept: (x.department || "medical").toLowerCase(),
            title: x.title || "Produit",
            desc: x.desc || "",
            ref: x.ref || "SUR-DEMANDE",
            imageUrl: x.imageUrl || "",
            category: x.category || "Autres",
            createdAt: x.createdAt || null,
          };
        });
        setRaw(docs);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.size === PAGE_SIZE);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);
/* ------ Works helpers (mêmes règles que Works.jsx) ------ */
const isVideo = (m) =>
  m?.type === "video" || (typeof m?.url === "string" && /\.(mp4|webm|ogg)(\?|#|$)/i.test(m.url));
const isImage = (m) =>
  m?.type === "image" || (typeof m?.url === "string" && /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(m.url));

const normalizeGallery = (w) => {
  if (Array.isArray(w.gallery) && w.gallery.length) return w.gallery;
  if (w.cover) return [{ type: "image", url: w.cover }];
  if (w.imageUrl) return [{ type: "image", url: w.imageUrl }]; // legacy
  return [];
};

const parseDate = (d, createdAt) => {
  if (typeof d === "string" && d.trim()) {
    const val = d.length === 7 ? `${d}-01` : d; // YYYY-MM -> 1er du mois
    const t = Date.parse(val);
    if (!Number.isNaN(t)) return new Date(t);
  }
  if (createdAt && typeof createdAt?.seconds === "number") {
    return new Date(createdAt.seconds * 1000);
  }
  return new Date(0);
};
const fmtMonthYear = (d) => (d ? d.toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "");

/* -------- Réalisations récentes (works) -------- */
const [works, setWorks] = useState([]);
const [loadingWorks, setLoadingWorks] = useState(true);

useEffect(() => {
  const colW = collection(db, "works");
  // Tri primaire par "date" (YYYY-MM), on limite à 3 cartes
  const qW = query(colW, orderBy("date", "desc"), limit(3));
  const unsub = onSnapshot(
    qW,
    (snap) => {
      const arr = snap.docs.map((d) => {
        const x = d.data() || {};
        const gallery = normalizeGallery(x);
        const firstImg = gallery.find(isImage);
        const firstAny = gallery[0];
        const cover = firstImg?.url || firstAny?.url || x.cover || x.imageUrl || "";
        const dateObj = parseDate(x.date, x.createdAt);
        return {
          id: d.id,
          title: x.title || "Réalisation",
          department: (x.department || "medical").toLowerCase(),
          place: x.place || "",
          blurb: x.blurb || "",
          date: x.date || "",
          dateLabel: fmtMonthYear(dateObj),
          cover,
          mediaTotal: gallery.length,
          hasVideo: gallery.some(isVideo),
        };
      });
      setWorks(arr);
      setLoadingWorks(false);
    },
    () => setLoadingWorks(false)
  );
  return () => unsub();
}, []);


  useEffect(() => {
    const colR = collection(db, "testimonials");
    const qR = query(colR, orderBy("createdAt", "desc"), limit(6));
    const unsubR = onSnapshot(qR, (snap) => {
      setReviews(
        snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            name: x.name || "Client",
            role: x.role || "",
            message: x.message || "",
            rating: Number(x.rating ?? 5),
            avatar: x.avatar || "",
          };
        })
      );
      setLoadingReviews(false);
    }, () => setLoadingReviews(false));
    return () => unsubR();
  }, []);

  /* -------- Pagination : charger plus -------- */
  const loadMore = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;
    setLoadingMore(true);
    const col = collection(db, "products");
    const qy = query(col, orderBy("createdAt", "desc"), startAfter(lastDoc), limit(PAGE_SIZE));
    const snap = await getDocs(qy);
    const docs = snap.docs.map((d) => {
      const x = d.data() || {};
      return {
        id: d.id,
        dept: (x.department || "medical").toLowerCase(),
        title: x.title || "Produit",
        desc: x.desc || "",
        ref: x.ref || "SUR-DEMANDE",
        imageUrl: x.imageUrl || "",
        category: x.category || "Autres",
        createdAt: x.createdAt || null,
      };
    });
    setRaw((prev) => [...prev, ...docs]);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === PAGE_SIZE);
    setLoadingMore(false);
  };

  /* -------- Découpage par département -------- */
  const medAll = useMemo(() => raw.filter((p) => p.dept === "medical"), [raw]);
  const indAll = useMemo(() => raw.filter((p) => p.dept !== "medical"), [raw]);

  const med9 = medAll.slice(0, MED_LIMIT);
  const indSome = indAll.slice(0, IND_LIMIT);

  // si on n’a pas encore assez d’items pour atteindre les quotas, on propose de charger plus
  const needMoreForQuota =
    hasMore && (med9.length < MED_LIMIT || indSome.length < IND_LIMIT);

  // KPIs simples (produits / catégories)
  const categoriesCount = useMemo(
    () => new Set(raw.map((p) => p.category || "Autres")).size,
    [raw]
  );
  const [kpiRef, kpiInView] = useInView();
  const countProducts = useCountUp(raw.length || 200, kpiInView, 800);
  const countCats = useCountUp(categoriesCount || 20, kpiInView, 800);

  const Stars = ({ n = 5 }) => (
    <span aria-label={`${n} sur 5`} title={`${n}/5`}>
      {"★".repeat(Math.max(0, Math.min(5, Math.round(n))))}
      {"☆".repeat(5 - Math.max(0, Math.min(5, Math.round(n))))}
    </span>
  );

  return (
    <div>
      {/* HERO + OVERLAP WRAPPER */}
      <div className="hero-overlap-wrapper position-relative">
        <HeroSection />

        {/* Ombre douce pour séparation visuelle */}
        <div className="hero-bottom-fade" aria-hidden="true" />
      </div>

      {/* PRODUITS – la section commence en chevauchant le bas du hero */}
      <div
        id="featured"
        className="featured-overlap pt-5 pb-3"   // évite py-5 trop généreux
        style={{
          '--overlap': '46dvh',                   // ajuste 20–30dvh selon le rendu
          transform: 'translateY(calc(-1 * var(--overlap)))',
          paddingTop: 'calc(var(--overlap) + 12px)',
          marginBottom: 'calc(-1 * var(--overlap))',  // ← supprime l’espace après
          position: 'relative',
          zIndex: 10,
          background: 'transparent'
        }}
      >

        <div className="container">
          <div className="d-flex justify-content-between align-items-end mb-3">
            <div>
              <h2 className="fw-bold mb-0"></h2>
            </div>
            <div className="d-none d-md-flex gap-2 hero-cta">
              <Link
                to="/medical/products"
                className="btn btn-outline-success btn-sm rounded-pill btn-on-media"
                onClick={() => window.scrollTo(0, 0)}
              >
                Catalogue Médical
              </Link>

              <Link
                to="/industrial/products"
                className="btn btn-outline-secondary btn-sm rounded-pill btn-on-media"
                onClick={() => window.scrollTo(0, 0)}
              >
                Catalogue Industriel
              </Link>
            </div>

          </div>

          {/* Médical */}
          <div className="mb-3 d-flex justify-content-between align-items-baseline">
            <h3 className="h5 fw-bold mb-0">Médical</h3>
            <Link to="/medical/products" className="small" onClick={() => window.scrollTo(0, 0)}>
              Voir tout
            </Link>
          </div>
          {loading ? (
            <div className="row g-3 mb-4">
              {Array.from({ length: MED_LIMIT }).map((_, i) => (
                <div className="col-6 col-md-4 col-lg-4" key={`med-skel-${i}`}>
                  <div className="card h-100 shadow-sm">
                    <div className="deal-thumb placeholder" />
                    <div className="card-body">
                      <div className="placeholder-glow">
                        <span className="placeholder col-8" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : med9.length === 0 ? (
            <div className="alert alert-light border mb-4">Aucun produit médical pour le moment.</div>
          ) : (
            <div className="row g-3 mb-4">
              {med9.map((p) => (
                <div className="col-6 col-md-4 col-lg-4" key={p.id}>
                  <div className="card h-100 shadow-sm deal-card" style={{ borderRadius: "15px" }}>
                    <Link
                      to={`/product/${p.id}`}
                      className="deal-thumb d-block"
                      aria-label={`Voir ${p.title}`}
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.title} />
                      ) : (
                        <img
                          alt="placeholder"
                          src={
                            "data:image/svg+xml;utf8," +
                            encodeURIComponent(
                              `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='533'>
                                 <rect width='100%' height='100%' fill='%23f8f9fa'/>
                                 <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                                   fill='%239aa0a6' font-family='Arial' font-size='20'>Image indisponible</text>
                               </svg>`
                            )
                          }
                        />
                      )}
                    </Link>
                    <div className="card-body d-flex flex-column">
                      <h6 className="deal-title mb-1">
                        <Link className="text-decoration-none" to={`/product/${p.id}`} onClick={() => window.scrollTo(0, 0)}>
                          {p.title}
                        </Link>
                      </h6>
                      <ul className="deal-meta small text-muted mb-3">
                        {p.category && <li>{p.category}</li>}
                        <li>Réf. {p.ref || "—"}</li>
                        <li>Médical</li>
                      </ul>
                      <div className="mt-auto d-grid gap-2">
                        <Link
                          className="btn btn-primary btn-lg"
                          to={`/contact?subjectType=${encodeURIComponent("Demande de devis")}&product=${encodeURIComponent(p.title || "")}&ref=${encodeURIComponent(p.ref || "")}`}
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          Demander en ligne
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Industriel */}
          <div className="mb-3 d-flex justify-content-between align-items-baseline">
            <h3 className="h5 fw-bold mb-0">Industriel</h3>
            <Link to="/industrial/products" className="small" onClick={() => window.scrollTo(0, 0)}>
              Voir tout
            </Link>
          </div>
          {loading ? (
            <div className="row g-3">
              {Array.from({ length: IND_LIMIT }).map((_, i) => (
                <div className="col-6 col-md-4 col-lg-4" key={`ind-skel-${i}`}>
                  <div className="card h-100 shadow-sm">
                    <div className="deal-thumb placeholder" />
                    <div className="card-body">
                      <div className="placeholder-glow">
                        <span className="placeholder col-8" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : indSome.length === 0 ? (
            <div className="alert alert-light border">Aucun produit industriel pour le moment.</div>
          ) : (
            <div className="row g-3">
              {indSome.map((p) => (
                <div className="col-6 col-md-4 col-lg-4" key={p.id}>
                  <div className="card h-100 shadow-sm deal-card">
                    <Link
                      to={`/product/${p.id}`}
                      className="deal-thumb d-block"
                      aria-label={`Voir ${p.title}`}
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.title} />
                      ) : (
                        <img
                          alt="placeholder"
                          src={
                            "data:image/svg+xml;utf8," +
                            encodeURIComponent(
                              `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='533'>
                                 <rect width='100%' height='100%' fill='%23f8f9fa'/>
                                 <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                                   fill='%239aa0a6' font-family='Arial' font-size='20'>Image indisponible</text>
                               </svg>`
                            )
                          }
                        />
                      )}
                    </Link>
                    <div className="card-body d-flex flex-column">
                      <h6 className="deal-title mb-1">
                        <Link className="text-decoration-none" to={`/product/${p.id}`} onClick={() => window.scrollTo(0, 0)}>
                          {p.title}
                        </Link>
                      </h6>
                      <ul className="deal-meta small text-muted mb-3">
                        {p.category && <li>{p.category}</li>}
                        <li>Réf. {p.ref || "—"}</li>
                        <li>Industriel</li>
                      </ul>
                      <div className="mt-auto d-grid gap-2">
                        <Link
                          className="btn btn-primary btn-lg"
                          to={`/contact?subjectType=${encodeURIComponent("Demande de devis")}&product=${encodeURIComponent(p.title || "")}&ref=${encodeURIComponent(p.ref || "")}`}
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          Demander en ligne
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Charger plus pour compléter les quotas */}
          {needMoreForQuota && (
            <div className="d-grid mt-3">
              <button className="btn btn-outline-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Chargement…" : "Charger plus de produits"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* DÉPARTEMENTS */}
      <div className="container py-5">
        <SectionTitle
          title="Nos départements"
          subtitle="Une offre complète pour vos besoins médicaux et industriels."
          center
        />
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body text-center">
                <div className="badge bg-success mb-2">Médical</div>
                <h5 className="fw-bold">Équipements & consommables de santé</h5>
                <p className="text-muted small">
                  Imagerie, diagnostic, mobilier médical, réactifs et stérilisation.
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <Link to="/medical/products" className="btn btn-success btn-cta btn-sm" onClick={() => window.scrollTo(0, 0)}>
                    Parcourir le catalogue →
                  </Link>
                  <a className="btn btn-outline-dark btn-sm" href={wapp("Bonjour, je cherche des équipements médicaux.")} target="_blank" rel="noreferrer">
                    Parler à un conseiller
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body text-center">
                <div className="badge bg-secondary mb-2">Industriel</div>
                <h5 className="fw-bold">Solutions & équipements professionnels</h5>
                <p className="text-muted small">Pesage, EPI, instrumentation, fournitures et projets sur mesure.</p>
                <div className="d-flex gap-2 justify-content-center">
                  <Link to="/industrial/products" className="btn btn-outline-secondary btn-cta btn-sm" onClick={() => window.scrollTo(0, 0)}>
                    Voir les solutions →
                  </Link>
                  <Link to="/contact" className="btn btn-outline-dark btn-sm" onClick={() => window.scrollTo(0, 0)}>
                    Demander un audit
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SERVICES */}
      <div className="bg-light border-top border-bottom">
        <div className="container py-5">
          <SectionTitle
            title="Nos services"
            subtitle="Accompagnement de l’étude à la mise en service, puis au-delà."
            center
          />
          <div className="row g-3">
            <div className="col-6 col-md-3"><ServiceCard icon="🔧" title="Installation" text="Mise en service et configuration sur site." /></div>
            <div className="col-6 col-md-3"><ServiceCard icon="🧪" title="Calibration" text="Contrôles & traçabilité documentée." /></div>
            <div className="col-6 col-md-3"><ServiceCard icon="🛠️" title="Maintenance" text="Préventif & curatif, pièces d’origine." /></div>
            <div className="col-6 col-md-3"><ServiceCard icon="🎓" title="Formation" text="Prise en main & bonnes pratiques." /></div>
          </div>
        </div>
      </div>

      {/* MARQUES */}
      <div className="border-bottom">
        <div className="container py-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3" role="list">
            {BRANDS.map((b) => (
              <div key={b.name} className="px-2 cta-lift" role="listitem" aria-label={b.name}>
                <img
                  src={b.src}
                  alt={b.name}
                  style={{ height: 40, objectFit: "contain", filter: "grayscale(80%)", opacity: 0.9 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RÉALISATIONS */}
<div className="bg-light border-top">
  <div className="container py-5">
    <div className="d-flex justify-content-between align-items-end mb-3">
      <h2 className="fw-bold mb-0">Réalisations récentes</h2>
      <div className="d-none d-sm-flex gap-2">
        <Link to="/medical/works" className="btn btn-outline-dark btn-sm" onClick={() => window.scrollTo(0, 0)}>
          Médical
        </Link>
        <Link to="/industrial/works" className="btn btn-outline-dark btn-sm" onClick={() => window.scrollTo(0, 0)}>
          Industriel
        </Link>
      </div>
    </div>

    {loadingWorks ? (
      <div className="row g-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div className="col-12 col-md-4" key={i}>
            <div className="card h-100 shadow-sm">
              <div className="product-thumb aspect-16x9 placeholder" />
              <div className="card-body">
                <h6 className="placeholder-glow"><span className="placeholder col-8"></span></h6>
                <p className="small text-muted placeholder-glow"><span className="placeholder col-12"></span></p>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : works.length === 0 ? (
      <div className="alert alert-light border">Aucune réalisation pour le moment.</div>
    ) : (
      <div className="row g-3">
        {works.map((w) => (
          <div className="col-12 col-md-4" key={w.id}>
            <div className="card h-100 shadow-sm cta-lift">
              {/* Vignette */}
              <div className="position-relative product-thumb aspect-16x9 rounded-top overflow-hidden bg-white">
                {w.cover ? (
                  <img
                    src={w.cover}
                    alt={w.title}
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                    loading="lazy"
                  />
                ) : (
                  <img
                    alt="placeholder"
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                    src={
                      "data:image/svg+xml;utf8," +
                      encodeURIComponent(
                        `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='450'>
                           <rect width='100%' height='100%' fill='%23f8f9fa'/>
                           <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                             fill='%239aa0a6' font-family='Arial' font-size='20'>Image indisponible</text>
                         </svg>`
                      )
                    }
                  />
                )}

                {/* Badges overlay — auto width, pas de “bandes” */}
                <div className="position-absolute top-0 start-0 p-2 d-flex align-items-start gap-2">
                  <span className={`badge ${w.department === "industrial" ? "text-bg-secondary" : "text-bg-success"} align-self-start`}>
                    {w.department === "industrial" ? "Industriel" : "Médical"}
                  </span>
                  {w.dateLabel && <span className="badge text-bg-dark align-self-start">{w.dateLabel}</span>}
                </div>
              </div>

              {/* Corps */}
              <div className="card-body">
                <h6 className="fw-bold mb-1">{w.title}</h6>
                {(w.place || w.blurb) && (
                  <p className="small text-muted mb-2 line-clamp-3">
                    {w.place}{w.place && w.blurb ? " — " : ""}{w.blurb}
                  </p>
                )}
                <Link
                  className="btn btn-outline-primary btn-sm"
                  to={`/${w.department}/works`}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Voir
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    {/* CTA mobile */}
    <div className="d-flex d-sm-none gap-2 justify-content-center mt-3">
      <Link to="/medical/works" className="btn btn-outline-dark btn-sm" onClick={() => window.scrollTo(0, 0)}>
        Médical
      </Link>
      <Link to="/industrial/works" className="btn btn-outline-dark btn-sm" onClick={() => window.scrollTo(0, 0)}>
        Industriel
      </Link>
    </div>
  </div>
</div>

      {/* AVIS */}
      <div className="border-top">
        <div className="container py-5">
          <SectionTitle
            title="Ce que disent nos clients"
            subtitle="Des retours concrets sur nos installations et notre support."
            center
          />
          {loadingReviews ? (
            <div className="row g-3">
              {[1, 2, 3].map((i) => (
                <div className="col-12 col-md-4" key={i}>
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <div className="placeholder-glow mb-2"><span className="placeholder col-6"></span></div>
                      <p className="small text-muted placeholder-glow"><span className="placeholder col-12"></span></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="alert alert-light border">Pas encore d’avis publiés.</div>
          ) : (
            <div className="row g-3">
              {reviews.map((r) => (
                <div className="col-12 col-md-4" key={r.id}>
                  <div className="card h-100 shadow-sm cta-lift">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <img
                          src={r.avatar || "https://via.placeholder.com/72x72?text=🙂"}
                          alt={r.name}
                          className="rounded-circle"
                          style={{ width: 36, height: 36, objectFit: "cover" }}
                        />
                        <div>
                          <strong>{r.name}</strong>
                          <div className="small text-muted">{r.role}</div>
                        </div>
                      </div>
                      <div className="text-warning mb-1"><Stars n={r.rating} /></div>
                      <p className="small mb-0">{r.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-3">
            <Link to="/contact" className="btn btn-success btn-cta" onClick={() => window.scrollTo(0, 0)}>
              Demander un devis personnalisé →
            </Link>
          </div>
        </div>
      </div>

      {/* BANNIÈRE CTA */}
      <div className="border-top" style={{ background: "linear-gradient(135deg,#E8F5E9,#C8E6C9)" }}>
        <div className="container py-5 text-center">
          <h3 className="fw-bold">Un projet ou une demande spécifique ?</h3>
          <p className="text-muted mb-3">Expliquez-nous vos contraintes : nous adaptons la solution, le budget et les délais.</p>
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <a className="btn btn-success btn-cta" href={wapp("Bonjour MAMECHE, j’ai un projet à étudier.")} target="_blank" rel="noreferrer">
              Obtenir un devis rapide →
            </a>
            <a className="btn btn-outline-dark" href={mailto("Demande d’informations", "Bonjour,\n\nJe souhaite échanger sur mon besoin :\n- \n- \n\nCordialement,")}>
              Échanger par email
            </a>
            <Link className="btn btn-outline-secondary" to="/contact" onClick={() => window.scrollTo(0, 0)}>
              Formulaire de contact
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}



